/**
 * Create listing — what the client posts to `POST /api/listings/create`.
 *
 * Wire-truthful 2-variant union, discriminated by `cardCategory`:
 *  - `cardCategory: "trading_card"` (+ `cardDraft`) → card listing,
 *    handled by `handleCardCreate` (writes `listings` + `listing_card_details`
 *    + optional `listing_grading_details` / `listing_centering` /
 *    `listing_card_defects`).
 *  - `cardCategory` absent → lego/regular listing, handled by
 *    `handleLegoCreate` (writes `listings`; supports taxonomy + commerce).
 *
 * The route dispatches on `body.cardCategory === "trading_card"` literal —
 * the schema mirrors that exactly. Three caller surfaces today:
 *  - `components/publications/.../card-review-step.tsx` → card variant
 *  - `components/publications/.../review-step.tsx` (lego review) → lego variant
 *  - `components/chat/ChatSellItemSheet.tsx` → lego variant with
 *    `visibility: 'chat_private'` (the route reads the visibility tag and
 *    zeroes quantity; visibility is a runtime value on the lego shape, not
 *    a separate wire kind — Phase 4 [2026-06-29] of
 *    `shippo-listing-discriminator` replaced the legacy `is_chat_private`
 *    boolean wire field). Mobile
 *    (`apps/mobile/components/sell-flow/index.tsx`) emits both variants
 *    through `apps/mobile/lib/listing-create-api.ts`.
 *
 * The `cardDraft` payload is intentionally permissive (`Record<string, unknown>`):
 * the route reads ~25 optional fields off it (`tcgId`, `tcgSetId`, `tcgRarityId`,
 * `conditionId`, `languageId`, `format`, `cardName`, `cardNumber`, `printYear`,
 * `specialAttributes`, `grading.{companyId,gradeId,certNumber,bgsSubgrades}`,
 * `centering`, `surfaceDefects`, etc.). Pinning the structural minimum would
 * over-constrain — the route is the strict gate, the contract is the additive
 * wire shape.
 *
 * Shared fields are also permissive (`shipping_config: unknown[]`, image
 * arrays as `string[]`) — the route normalises and refilters per its own
 * business rules. Tightening any of these is breaking for already-shipping
 * producers (web + mobile).
 *
 * @see app/api/listings/create/route.ts — handler (boundary safeParse lands
 *      with this contract)
 * @see lib/listings/listing-create.ts — canonical web builder
 * @see apps/mobile/lib/listing-create-api.ts — canonical mobile builder
 * @see components/publications/creation-flow/listing-creation/steps/card-review-step.tsx
 * @see components/publications/creation-flow/listing-creation/steps/review-step.tsx
 * @see components/chat/ChatSellItemSheet.tsx
 * @see apps/mobile/components/sell-flow/index.tsx — mobile producer (lego + card)
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-E F1 (this contract)
 */

import { z } from "zod";
import { CARRIER_PROVIDERS, type CarrierProvider } from "./carrier";
import {
  LISTING_FORMATS,
  LISTING_VISIBILITIES,
  type ListingVisibility,
} from "./listing-adt";
import { ISO_4217_CODES, type Iso4217 } from "./money";
import {
  MIN_PARCEL_HEIGHT_MM,
  MIN_PARCEL_LENGTH_MM,
  MIN_PARCEL_WEIGHT_GRAMS,
  MIN_PARCEL_WIDTH_MM,
} from "./parcel-min-dims";
import { ComicDraftSchema, type ComicDraft } from "./listing-comic";
import { pinned } from "./pin";
import { rawImageRefField, type RawImageRef } from "./raw-image-ref";

/**
 * Fields every variant accepts. Optional because producers conditionally
 * include them (e.g. ChatSellItemSheet hard-codes `visibility:
 * 'chat_private'` and never sets `is_dev_listing`; card-review-step always
 * sends `condition: ""` + `grade: ""` while review-step fills them in).
 * Keeping them optional matches the legacy wire byte-for-byte per producer.
 *
 * `shipping_config` accepts `unknown[]` because the route's
 * `normalizeShippingConfig` accepts both legacy stringified and array forms;
 * producers always emit an array, but tightening would reject pre-mobile-lock
 * clients shipping the legacy shape from cached state.
 *
 * Phase 4 (2026-06-29) of `shippo-listing-discriminator` replaced the legacy
 * `is_chat_private` boolean wire field with the `visibility` enum
 * (`public` / `chat_private`). The boolean column is gone on the DB; the
 * wire shape mirrors the new column directly.
 */
interface CommonListingFields {
  id?: string;
  title?: string;
  description?: string | null;
  price?: number | null;
  quantity?: number;
  shipping_config?: unknown[];
  /**
   * PERSISTENCE array — see the twin on `listing-patch.ts::CommonPatchFields`.
   */
  image_urls?: RawImageRef[];
  condition?: string;
  grade?: string;
  scheduled_publish_at?: string | null;
  is_dev_listing?: boolean;
  /**
   * Phase 4 (2026-06-29) of `shippo-listing-discriminator`: orthogonal
   * visibility axis. `chat_private` rows live on the same `listings` table
   * as public rows; the chat surface filters by `visibility = 'chat_private'`
   * and the route force-zeroes quantity on create. Default (absent) ≡
   * `public` per the DB column default.
   */
  visibility?: ListingVisibility;
  chat_private_saved?: boolean;
  /** Both casings present on the wire — review-step uses snake, mobile sell-flow uses neither, the route reads both via `||`. Keep both fields. */
  skip_notifications?: boolean;
  skipNotifications?: boolean;
  /**
   * Shippo white-label (WS3). Discriminator pair `(shipping_mode, carrier_provider)`
   * mirrors the DB CHECKs on `listings`:
   *  - `shipping_mode='legacy'`    ↔ carrier_provider null + parcel_* null
   *  - `shipping_mode='live_rate'` ↔ carrier_provider non-null + parcel_* required
   *
   * Default is `legacy` server-side — producers that don't set it keep
   * legacy flat-rate behaviour byte-for-byte (no behavioural diff for
   * existing card-review-step / review-step / ChatSellItemSheet flows).
   */
  shipping_mode?: "legacy" | "live_rate";
  carrier_provider?: CarrierProvider | null;
  /**
   * Canonical parcel dimensions (locked 2026-06-22, policy C in PLAN §A
   * "Parcel units"): mass in grams, distances in millimetres. DB columns
   * renamed to `_grams` / `_mm` in K.6 bundled migration (2026-06-23).
   *
   * Slice C2 (2026-06-23) — the legacy `parcel_weight` / `parcel_length` /
   * `parcel_width` / `parcel_height` + `parcel_mass_unit` /
   * `parcel_distance_unit` transitional fields are GONE. UI form converts
   * locale → canonical before POST.
   */
  parcel_weight_grams?: number | null;
  parcel_length_mm?: number | null;
  parcel_width_mm?: number | null;
  parcel_height_mm?: number | null;
  /**
   * Per-listing ship-from override (ADR_LISTING_SHIP_FROM_ADDRESS.md
   * Phase 1). UUID of an `address_book` row owned by the listing's
   * seller. Absent / undefined → resolves through the seller's
   * `is_main` row at rate-fetch time (legacy behaviour).
   *
   * Additive optional — mobile and any other producer that does not
   * send the field stays green.
   */
  ship_from_address_id?: string;
  /**
   * ISO 4217 currency code. Accepted on the wire but IGNORED by the
   * server: the listings BEFORE trigger overwrites `currency` from
   * `profiles.currency` (ADR_CURRENCY_PIN — pivoted 2026-06-23 from
   * country-pin to profile-pin). Kept in the contract so legacy clients
   * sending it don't 400; trigger is the enforcement.
   */
  currency?: string;
}

/**
 * Commerce fields. Present on both variants — auction/offer mechanics work
 * the same for card and lego (card-review-step at lines 230-247,
 * review-step at lines 274-293 emit the identical commerce-conditional
 * branch). Preorder fields (`listing_type: "preorder"` + `preorder_*`,
 * `estimated_delivery`, `min_orders`, `max_orders`) ride alongside.
 *
 * Phase 3 (2026-06-29) of `shippo-listing-discriminator` collapsed the
 * legacy `accepts_offers` boolean into `listing_format` — the discriminator
 * is now the single source of truth, derived through
 * `isOfferEnabledFormat()` in `@kraahl/contract`. The boolean field is no
 * longer on the wire shape; mobile + web producers send the format only.
 */
interface CommerceFields {
  listing_format?:
    | "fixed_price"
    | "fixed_price_with_offers"
    | "offers_only"
    | "auction";
  auto_decline_below?: number | null;
  auction_start_price?: number;
  auction_reserve_price?: number | null;
  listing_type?: "preorder";
  preorder_deadline?: string | null;
  estimated_delivery?: string | null;
  min_orders?: number | null;
  max_orders?: number | null;
}

/**
 * Taxonomy fields — lego variant only. Card listings resolve their
 * taxonomy via `cardDraft.tcgId` / `cardDraft.tcgSetId` / etc., NOT via
 * these top-level ids. The route's `resolveTaxonomyIdsFromPayload` only
 * runs on the lego branch.
 */
interface TaxonomyFields {
  brand_id?: string | null;
  theme_id?: string | null;
  product_type_id?: string | null;
  brand_custom_text?: string | null;
  theme_custom_text?: string | null;
  product_type_custom_text?: string | null;
}

export interface CreateListingRequestCard
  extends CommonListingFields,
    CommerceFields {
  cardCategory: "trading_card";
  /**
   * Card-specific draft. The route validates the structurally-significant
   * subset (`tcgId`/`tcgSetId`/`tcgRarityId`/`conditionId`/`languageId`
   * against `taxonomy_items`, `grading.companyId`/`gradeId` against
   * `card_grade_scales`, BGS sub-scores all-or-none, `centering.leftPct`
   * / `centering.topPct` in [50, 90]); display fields ride passthrough.
   */
  cardDraft: Record<string, unknown>;
}

export interface CreateListingRequestComic
  extends CommonListingFields,
    CommerceFields {
  /** D1 (2026-07-08): the legacy-named `cardCategory` field IS the category
   * wire discriminator — widened per category, not renamed. */
  cardCategory: "comic";
  /** Typed draft — parsed by `ComicDraftSchema` at the boundary (upgrade
   * over the opaque `cardDraft`; see listing-comic.ts header). */
  comicDraft: ComicDraft;
}

export interface CreateListingRequestLego
  extends CommonListingFields,
    CommerceFields,
    TaxonomyFields {
  /** Absence is the discriminator — `cardCategory: undefined` ≡ not a card. */
  cardCategory?: undefined;
}

export type CreateListingRequest =
  | CreateListingRequestCard
  | CreateListingRequestComic
  | CreateListingRequestLego;

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

const CommonListingShape = {
  id: z.string().optional(),
  title: z.string().optional(),
  description: z.string().nullish(),
  price: z.number().nullish(),
  quantity: z.number().optional(),
  shipping_config: z.array(z.unknown()).optional(),
  image_urls: z.array(rawImageRefField()).optional(),
  condition: z.string().optional(),
  grade: z.string().optional(),
  scheduled_publish_at: z.string().nullish(),
  is_dev_listing: z.boolean().optional(),
  // Phase 4 (2026-06-29) of `shippo-listing-discriminator` — see
  // CommonListingFields.visibility.
  visibility: z.enum(LISTING_VISIBILITIES).optional(),
  chat_private_saved: z.boolean().optional(),
  skip_notifications: z.boolean().optional(),
  skipNotifications: z.boolean().optional(),
  shipping_mode: z.enum(["legacy", "live_rate"]).optional(),
  carrier_provider: z.enum(CARRIER_PROVIDERS).nullish(),
  // Canonical (locked 2026-06-22, K.6) — server writes these. Slice C2
  // (2026-06-23) dropped the legacy locale-shape fields; UI form converts.
  //
  // Floor pin (Option A, 2026-06-30): tightened from `.positive()` to
  // `.min(MIN_*)` so a publish/edit cannot save sub-carrier-minimum dims.
  // Constants live in `@kraahl/contract/parcel-min-dims` (single SoT,
  // shared with the rate-quote-time domain gate in `lib/shipping/rates.ts`
  // and mobile via the same contract import).
  // @see .tondocs/shippo/PARCEL_MIN_DIMS.md §5 Option A
  parcel_weight_grams: z.number().min(MIN_PARCEL_WEIGHT_GRAMS).nullish(),
  parcel_length_mm: z.number().min(MIN_PARCEL_LENGTH_MM).nullish(),
  parcel_width_mm: z.number().min(MIN_PARCEL_WIDTH_MM).nullish(),
  parcel_height_mm: z.number().min(MIN_PARCEL_HEIGHT_MM).nullish(),
  // Per-listing ship-from override — additive optional, see CommonListingFields.
  ship_from_address_id: z.string().uuid().optional(),
  // Currency — accepted but ignored. Server trigger overwrites from
  // `profiles.currency`. See CommonListingFields.currency comment + ADR.
  currency: z.string().optional(),
} as const;

// Cross-field coherence: live_rate requires carrier_provider + all 6 parcel
// fields. Mirrors the listings DB CHECK `listings_provider_required_for_live_rate`
// + the route-level publish-time invariant. Refinement runs after the
// per-field z.enum/z.number guards, so by here individual types are pinned.
function refineLiveRateCoherence(
  body: {
    shipping_mode?: "legacy" | "live_rate";
    carrier_provider?: CarrierProvider | null;
    parcel_weight_grams?: number | null;
    parcel_length_mm?: number | null;
    parcel_width_mm?: number | null;
    parcel_height_mm?: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (body.shipping_mode !== "live_rate") return;
  if (body.carrier_provider == null) {
    ctx.addIssue({
      code: "custom",
      path: ["carrier_provider"],
      message: "carrier_provider is required when shipping_mode='live_rate'",
      params: { code: "LIVE_RATE_PARCEL_REQUIRED" },
    });
  }
  // Canonical-only (slice C2): all 4 _grams / _mm fields must be present.
  const canonicalComplete =
    body.parcel_weight_grams != null &&
    body.parcel_length_mm != null &&
    body.parcel_width_mm != null &&
    body.parcel_height_mm != null;
  if (!canonicalComplete) {
    ctx.addIssue({
      code: "custom",
      path: ["parcel_weight_grams"],
      message:
        "parcel dimensions are required when shipping_mode='live_rate' " +
        "(parcel_weight_grams + parcel_length_mm + parcel_width_mm + parcel_height_mm)",
      params: { code: "LIVE_RATE_PARCEL_REQUIRED" },
    });
  }
}

const CommerceShape = {
  // Single SoT: `LISTING_FORMATS` tuple lives in `./listing-adt`. Extending
  // it (Phase 6/7 of `shippo-listing-discriminator`) automatically widens
  // this schema — no parallel literal to keep in sync.
  listing_format: z.enum(LISTING_FORMATS).optional(),
  auto_decline_below: z.number().nullish(),
  auction_start_price: z.number().optional(),
  auction_reserve_price: z.number().nullish(),
  listing_type: z.literal("preorder").optional(),
  preorder_deadline: z.string().nullish(),
  estimated_delivery: z.string().nullish(),
  min_orders: z.number().nullish(),
  max_orders: z.number().nullish(),
} as const;

const TaxonomyShape = {
  brand_id: z.string().nullish(),
  theme_id: z.string().nullish(),
  product_type_id: z.string().nullish(),
  brand_custom_text: z.string().nullish(),
  theme_custom_text: z.string().nullish(),
  product_type_custom_text: z.string().nullish(),
} as const;

const CardListingRequestSchema = z
  .object({
    cardCategory: z.literal("trading_card"),
    cardDraft: z.record(z.string(), z.unknown()),
    ...CommonListingShape,
    ...CommerceShape,
  })
  .superRefine(refineLiveRateCoherence);

const ComicListingRequestSchema = z
  .object({
    cardCategory: z.literal("comic"),
    comicDraft: ComicDraftSchema,
    ...CommonListingShape,
    ...CommerceShape,
  })
  .superRefine(refineLiveRateCoherence);

const LegoListingRequestSchema = z
  .object({
    cardCategory: z.undefined().optional(),
    ...CommonListingShape,
    ...CommerceShape,
    ...TaxonomyShape,
  })
  .superRefine(refineLiveRateCoherence);

// Lego (cardCategory absent) must stay LAST — it is the catch-all member.
export const CreateListingRequestSchema = pinned<CreateListingRequest>()(
  z.union([
    CardListingRequestSchema,
    ComicListingRequestSchema,
    LegoListingRequestSchema,
  ]),
);

// ---------------------------------------------------------------------------
// Response — F-04 (2026-06-24)
// ---------------------------------------------------------------------------

/**
 * Response shape for `POST /api/listings/create` (both lego + card variants).
 *
 * `currency` echo is REQUIRED so the client can detect the silent overwrite
 * performed by the K.6 BEFORE-trigger on `listings`: the trigger pins
 * `listings.currency := profiles.currency` regardless of what the client
 * submitted. Without an echo, a client that POSTed EUR keeps believing the
 * listing is EUR while the DB has USD — silent boundary drift. The client
 * compares the echo against what it sent and surfaces a warning UI on
 * divergence (`currency_was_pinned` semantics).
 *
 * @see .tondocs/payments/ADR_CURRENCY_PIN.md
 * @see app/api/listings/create/route.ts (lego + card success returns)
 */
export interface CreateListingResponse {
  listingId: string;
  currency: Iso4217;
}

export const CreateListingResponseSchema = pinned<CreateListingResponse>()(
  z.object({
    listingId: z.string(),
    // The K.6 currency-pin trigger constrains listings.currency to the
    // Iso4217 union via profiles.currency. Single SoT: money.ts::ISO_4217_CODES.
    currency: z.enum(ISO_4217_CODES),
  }),
);
